// ---------------------------------------------------------------------------
// C5/C7: Density Mode Settings Page
// ---------------------------------------------------------------------------
// Settings → Display: Easy/Normal/Power toggle + per-module overrides.
// ---------------------------------------------------------------------------
import {
  useDensity,
  DENSITY_MODULES,
  type Density,
} from "@/providers/density-context";
import { Monitor, Smartphone, LayoutGrid } from "lucide-react";

// ---------------------------------------------------------------------------
// Density Option Card
// ---------------------------------------------------------------------------

const DENSITY_OPTIONS: Array<{
  value: Density;
  label: string;
  description: string;
  icon: typeof Monitor;
  preview: { rows: number; gap: string; fontSize: string };
}> = [
  {
    value: "easy",
    label: "Easy",
    description: "Spacious layout. Larger text. Generous padding. Best for new users.",
    icon: Smartphone,
    preview: { rows: 3, gap: "12px", fontSize: "15px" },
  },
  {
    value: "normal",
    label: "Normal",
    description: "Balanced layout. Standard density. Comfortable for everyday work.",
    icon: Monitor,
    preview: { rows: 5, gap: "8px", fontSize: "14px" },
  },
  {
    value: "power",
    label: "Power",
    description: "Dense layout. Compact rows. More data visible. For agencies and power users.",
    icon: LayoutGrid,
    preview: { rows: 8, gap: "4px", fontSize: "13px" },
  },
];

function DensityCard({
  option,
  isSelected,
  onSelect,
}: {
  option: typeof DENSITY_OPTIONS[number];
  isSelected: boolean;
  onSelect: () => void;
}) {
  const Icon = option.icon;

  return (
    <button
      onClick={onSelect}
      className={`
        flex flex-col items-center gap-3 rounded-xl border-2 p-5 text-center transition-all
        ${isSelected
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 shadow-md"
          : "border-border hover:border-[var(--color-primary)]/30 hover:shadow-sm"
        }
      `}
    >
      <Icon className={`h-8 w-8 ${isSelected ? "text-[var(--color-primary)]" : "text-muted-foreground"}`} />
      <div>
        <h3 className={`text-sm font-semibold ${isSelected ? "text-[var(--color-primary)]" : "text-foreground"}`}>
          {option.label}
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">{option.description}</p>
      </div>

      {/* Mini preview */}
      <div className="mt-2 w-full rounded-lg border border-border bg-muted/30 p-2">
        <div className="space-y-0.5" style={{ gap: option.preview.gap }}>
          {Array.from({ length: option.preview.rows }, (_, i) => (
            <div
              key={i}
              className="rounded bg-border/40"
              style={{ height: option.preview.fontSize, marginBottom: option.preview.gap }}
            />
          ))}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function DensitySettings() {
  const { global, moduleOverrides, setGlobal, setModuleOverride } = useDensity();

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Display Density</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose how much information you see at once. Changes apply immediately.
        </p>
      </div>

      {/* Global density selection */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">Global Density</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {DENSITY_OPTIONS.map((option) => (
            <DensityCard
              key={option.value}
              option={option}
              isSelected={global === option.value}
              onSelect={() => setGlobal(option.value)}
            />
          ))}
        </div>
      </div>

      {/* Per-module overrides (C7) */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-1">Module Overrides</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Override density for specific modules. Leave as "Global default" to use your global setting.
        </p>

        <div className="rounded-lg border border-border divide-y divide-border">
          {DENSITY_MODULES.map((mod) => {
            const override = moduleOverrides[mod.value];
            return (
              <div key={mod.value} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-foreground">{mod.label}</span>
                <select
                  value={override ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setModuleOverride(mod.value, val === "" ? null : val as Density);
                  }}
                  className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                >
                  <option value="">Global default ({global})</option>
                  <option value="easy">Easy</option>
                  <option value="normal">Normal</option>
                  <option value="power">Power</option>
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
